import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  Button,
  List,
  ListItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Inventory2Outlined as InventoryIcon,
} from '@mui/icons-material';
import { useShoppingCart } from '../../contexts/ShoppingCartContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Item, BatchStockInfo, InventoryMode, ItemType, PriceEntryMode } from '../../types';
import { apiClient as api } from '../../services/api';
import { rateLimitErrorMessage } from '../../utils/errorMessage';
import {
  calculateEnteredPriceBreakdown,
  calculateStoredPriceBreakdown,
  getCanonicalUnitPrice,
  SalePriceBreakdown,
} from '../../utils/salesPricing';
import { getPriceForEntryMode } from '../../utils/pricing';
import { formatDate } from '../../utils/date';
import { formatCurrency } from '../../utils/currency';
import { BarcodeScanButton } from '../barcode';
import { getInventoryMode, tracksBatches, tracksInventory } from '../../utils/inventoryMode';

interface ItemSelectorProps {
  onItemAdded?: (item: Item, quantity: number) => void;
  batchTrackingEnabled?: boolean;
}

export function ItemSelector({ onItemAdded, batchTrackingEnabled }: ItemSelectorProps) {
  const { addItem, state } = useShoppingCart();
  const { t, language } = useLanguage();
  
  const [items, setItems] = useState<Item[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Add item dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [servicePrice, setServicePrice] = useState('');
  const [availableBatches, setAvailableBatches] = useState<BatchStockInfo[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<string>('');
  const [isAddingItem, setIsAddingItem] = useState(false);
  const canUseBatchTracking = batchTrackingEnabled !== false;

  const numericQuantity = Number(quantity);
  const isQuantityValid = /^\d+$/.test(quantity) && Number.isSafeInteger(numericQuantity) && numericQuantity > 0;
  const isService = selectedItem?.itemType === ItemType.SERVICE;
  const numericServicePrice = Number(servicePrice);
  const isServicePriceValid = !isService
    || (servicePrice.trim() !== '' && Number.isFinite(numericServicePrice) && numericServicePrice > 0);
  const servicePriceEntryModeText = selectedItem?.priceEntryMode === PriceEntryMode.VAT_INCLUDED
    ? t('sales.priceEntryModeVatIncluded')
    : t('sales.priceEntryModeVatExcluded');
  const servicePriceHelpText = `${t('sales.servicePriceHelp')} ${t('sales.priceEntryModeHelp').replace('{mode}', servicePriceEntryModeText)}`;

  const searchItems = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setItems([]);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      const results = await api.searchItemsForSales(searchTerm);
      setItems(results || []);
    } catch (error: unknown) {
      setError(rateLimitErrorMessage(error, t('sales.failedToLoadItems'), t));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const timeoutId = setTimeout(() => searchItems(searchTerm), 300);
    return () => clearTimeout(timeoutId);
  }, [searchItems, searchTerm]);

  const getCurrentPricing = (): SalePriceBreakdown | null => {
    if (!selectedItem) return null;
    const safeQuantity = isQuantityValid ? numericQuantity : 1;
    const mode = selectedItem.priceEntryMode ?? PriceEntryMode.VAT_EXCLUDED;
    const rate = selectedItem.vatRate ?? 0;
    if (selectedItem.itemType === ItemType.SERVICE) {
      return calculateEnteredPriceBreakdown(numericServicePrice || 0, mode, rate, safeQuantity);
    }
    const batch = availableBatches.find(candidate => candidate.batchId === selectedBatch);
    const unitNet = batch?.unitPriceNet ?? selectedItem.price ?? selectedItem.priceGross ?? 0;
    const unitGross = batch?.unitPrice ?? selectedItem.priceGross ?? selectedItem.price ?? 0;
    return calculateStoredPriceBreakdown(
      unitNet,
      unitGross,
      batch?.vatRate ?? rate,
      safeQuantity,
      batch?.priceEntryMode ?? mode,
    );
  };

  const currentPricing = getCurrentPricing();
  const getCurrentPrice = () => currentPricing?.unitGrossPrice ?? 0;

  const sortItemsByName = useCallback((left: Item, right: Item) => {
    return left.name.localeCompare(right.name, language, { sensitivity: 'base' });
  }, [language]);

  const inStockItems = useMemo(
    () => items.filter((item) => !tracksInventory(item) || Number(item.totalStockQuantity) > 0).sort(sortItemsByName),
    [items, sortItemsByName]
  );

  const outOfStockItems = useMemo(
    () => items.filter((item) => tracksInventory(item) && Number(item.totalStockQuantity) <= 0).sort(sortItemsByName),
    [items, sortItemsByName]
  );

  const handleItemSelect = async (item: Item) => {
    setSelectedItem(item);
    setQuantity('1');
    setServicePrice(
      getPriceForEntryMode(item.price, item.priceGross, item.priceEntryMode)?.toString() || '',
    );
    setSelectedBatch('');
    
    // Load available batches if item has batch tracking
    if (canUseBatchTracking && tracksBatches(item)) {
      try {
        const batches = await api.getAvailableBatches(item.id);
        // Convert backend format to frontend format
        const formattedBatches: BatchStockInfo[] = batches.map(batch => ({
          batchId: batch.batchId,
          batchNumber: batch.batchNumber,
          availableQuantity: batch.availableQuantity,
          unitPrice: batch.unitPrice,
          unitPriceNet: batch.unitPriceNet,
          vatRate: batch.vatRate,
          priceEntryMode: batch.priceEntryMode,
          expirationDate: batch.expirationDate,
          productionDate: batch.productionDate
        }));
        setAvailableBatches(formattedBatches);
      } catch {
        setAvailableBatches([]);
      }
    } else {
      setAvailableBatches([]);
    }
    
    setShowAddDialog(true);
  };

  const handleBarcodeDetected = async (barcode: string) => {
    setSearchTerm(barcode);
    setError(null);
    setIsLoading(true);
    try {
      const item = await api.getItemByBarcode(barcode);
      setItems([item]);
      if (tracksInventory(item) && Number(item.totalStockQuantity) <= 0) {
        setError(t('sales.outOfStock'));
        return;
      }
      await handleItemSelect(item);
    } catch (error: unknown) {
      setError(rateLimitErrorMessage(error, t('sales.barcodeNotFound'), t));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!selectedItem || !state.selectedCustomer || !isQuantityValid || !isServicePriceValid) return;

    setIsAddingItem(true);
    try {
      const pricing = getCurrentPricing();
      if (!pricing) return;
      
      if (selectedBatch) {
        await addItem(
          selectedItem,
          numericQuantity,
          selectedBatch,
          availableBatches.find(batch => batch.batchId === selectedBatch)?.batchNumber,
          pricing,
        );
      } else {
        await addItem(selectedItem, numericQuantity, undefined, undefined, pricing);
      }
      
      setShowAddDialog(false);
      if (onItemAdded) {
        onItemAdded(selectedItem, numericQuantity);
      }
    } catch (error: unknown) {
      setError(rateLimitErrorMessage(error, t('sales.failedToLoadItems'), t));
    } finally {
      setIsAddingItem(false);
    }
  };


  const renderItem = (item: Item, outOfStock: boolean) => (
    <ListItem key={item.id} data-testid={`sales-item-row-${item.id}`}
      sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2, py: 1.5, px: 2, mb: 1, border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5, bgcolor: outOfStock ? 'action.hover' : 'background.paper' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={700}>{item.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('sales.itemNumber')}: {item.itemNumber}
          {item.barcode ? ` · ${t('items.barcode')}: ${item.barcode}` : ''}
        </Typography>
        {item.description && <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{item.description}</Typography>}
        {item.categories && item.categories.length > 0 &&
          <Typography variant="caption" color="text.secondary">{item.categories.map(category => category.name).join(', ')}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexShrink: 0 }}>
        <Box sx={{ textAlign: { sm: 'right' } }}>
          <Typography variant="subtitle1" fontWeight={700} color="primary.main">
            {item.itemType === ItemType.SERVICE
              ? t('sales.servicePriceHelp')
              : getInventoryMode(item) === InventoryMode.BATCHED
              ? t('items.priceVariesByBatch')
              : formatCurrency(getCanonicalUnitPrice(item, [], undefined))}
          </Typography>
          {item.itemType !== ItemType.SERVICE && getInventoryMode(item) !== InventoryMode.BATCHED && (
            <Typography variant="caption" color="text.secondary" display="block">
              {t('sales.netAmount')}: {formatCurrency(item.price ?? item.priceGross ?? 0)} · {t('sales.vatRate')}: %{item.vatRate ?? 0}
            </Typography>
          )}
          <Typography variant="caption" color={outOfStock ? 'error.main' : 'text.secondary'}>
            {!tracksInventory(item)
              ? t('sales.inventoryNotTracked')
              : outOfStock ? t('sales.outOfStock') : `${t('sales.stockQuantity')}: ${item.totalStockQuantity}`}
          </Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<AddIcon />}
          data-testid={`sales-item-add-${item.id}`} onClick={() => void handleItemSelect(item)}
          disabled={outOfStock} sx={{ minWidth: 94 }}>
          {t('sales.add')}
        </Button>
      </Box>
    </ListItem>
  );

  if (!state.selectedCustomer) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('sales.selectCustomer')}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t('sales.selectCustomerBeforeAdding')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: 'action.selected', color: 'primary.main' }}><InventoryIcon fontSize="small" /></Box>
            {t('sales.addItems')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Search Control */}
          <Box sx={{ mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
              <TextField
                fullWidth
                label={t('sales.searchItems')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                inputProps={{ 'data-testid': 'sales-item-search' }}
                placeholder={t('sales.searchItemsPlaceholder')}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                autoFocus
              />
              <BarcodeScanButton
                label={t('barcode.scan')}
                onDetected={(barcode) => { void handleBarcodeDetected(barcode); }}
              />
            </Stack>
          </Box>

          {/* Items List */}
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              data-testid="sales-item-results-scroll"
              sx={{
                maxHeight: { xs: 420, sm: 560 },
                overflowY: 'auto',
                pr: 1,
              }}
            >
            <List data-testid="sales-item-results" disablePadding>
              {inStockItems.length > 0 && <Typography data-testid="sales-in-stock-group" variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>{t('sales.inStockGroup')}</Typography>}
              {inStockItems.map(item => renderItem(item, false))}
              {outOfStockItems.length > 0 && <Typography data-testid="sales-out-of-stock-group" variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>{t('sales.outOfStockGroup')}</Typography>}
              {outOfStockItems.map(item => renderItem(item, true))}
              {items.length === 0 && !isLoading && (
                <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                  <InventoryIcon sx={{ fontSize: 38, opacity: .6, mb: 1 }} />
                  <Typography>{t(searchTerm.trim() ? 'sales.noItemsFound' : 'sales.searchItemsPlaceholder')}</Typography>
                </Box>
              )}
            </List>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Item Dialog */}
      <AppDialog presentation="compact" open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('sales.addToCart')}
        </DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedItem.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {isService ? servicePriceHelpText : `${t('sales.grossAmount')}: ${formatCurrency(getCurrentPrice())}`}
                  {canUseBatchTracking && tracksBatches(selectedItem) && selectedBatch && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({t('sales.batchPrice')})
                    </Typography>
                  )}
                </Typography>
                {selectedItem.description && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {selectedItem.description}
                  </Typography>
                )}
              </Box>

              {isService ? (
                <TextField
                  fullWidth
                  label={t('sales.price')}
                  type="number"
                  value={servicePrice}
                  onChange={event => setServicePrice(event.target.value)}
                  error={!isServicePriceValid}
                  helperText={!isServicePriceValid ? t('sales.servicePriceRequired') : servicePriceHelpText}
                  inputProps={{ min: 0.01, step: 0.01, inputMode: 'decimal', 'data-testid': 'sales-service-price' }}
                />
              ) : null}

              <TextField
                fullWidth
                label={t('sales.quantity')}
                type="number"
                value={quantity}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) setQuantity(value);
                }}
                onKeyDown={(e) => {
                  if (['e', 'E', '+', '-', '.', ','].includes(e.key)) e.preventDefault();
                }}
                error={!isQuantityValid}
                helperText={!isQuantityValid ? t('sales.invalidQuantity') : undefined}
                inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '[0-9]*', 'data-testid': 'sales-item-quantity' }}
                sx={{
                  '& input[type=number]': { MozAppearance: 'textfield' },
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />

              {canUseBatchTracking && tracksBatches(selectedItem) && availableBatches.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>{t('sales.selectBatch')}</InputLabel>
                  <Select
                    value={selectedBatch}
                    onChange={(e) => setSelectedBatch(e.target.value)}
                    label={t('sales.selectBatch')}
                  >
                      {availableBatches.map((batch) => (
                        <MenuItem key={batch.batchId} value={batch.batchId}>
                          <Box>
                            <Typography variant="body2">
                              {batch.batchNumber}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('sales.price')}: {formatCurrency(batch.unitPrice)} | {t('sales.available')}: {batch.availableQuantity}
                              {batch.expirationDate && (
                                <> | {t('sales.expires')}: {formatDate(batch.expirationDate, language)}</>
                              )}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}

              {canUseBatchTracking && tracksBatches(selectedItem) && availableBatches.length === 0 && (
                <Alert severity="warning">
                  {t('sales.noBatchesAvailable')}
                </Alert>
              )}

              {currentPricing && (
                <Box data-testid="sales-price-breakdown" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                  <Stack spacing={0.5}>
                    <Box display="flex" justifyContent="space-between" gap={2}>
                      <Typography variant="body2" color="text.secondary">{t('sales.netAmount')}</Typography>
                      <Typography variant="body2">{formatCurrency(currentPricing.netAmount)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" gap={2}>
                      <Typography variant="body2" color="text.secondary">{t('sales.vatAmount')} (%{currentPricing.vatRate})</Typography>
                      <Typography variant="body2">{formatCurrency(currentPricing.vatAmount)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" gap={2}>
                      <Typography fontWeight={700}>{t('sales.grossAmount')}</Typography>
                      <Typography fontWeight={700}>{formatCurrency(currentPricing.grossAmount)}</Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)} disabled={isAddingItem}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            data-testid="sales-add-to-cart"
              disabled={
                isAddingItem ||
                !isQuantityValid ||
                !isServicePriceValid ||
                Boolean(selectedItem && canUseBatchTracking && tracksBatches(selectedItem) && (!selectedBatch || availableBatches.length === 0))
            }
            startIcon={isAddingItem ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {isAddingItem ? t('sales.adding') : t('sales.addToCart')}
          </Button>
        </DialogActions>
      </AppDialog>
    </Box>
  );
}
